import React, { useEffect, useState } from "react";
import { FaStar, FaRegStar, FaCheckCircle } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

import Navbar from "../nav_bar/Navbar";
import {
  fetchAllReviews,
  fetchReviewSummary,
  deleteMyReview,
  addOrUpdateReview,
} from "../../services/reviewService";
import AddReviewModal from "../review/AddReviewModal";

import "./ReviewsPage.css";

const ReviewsPage = () => {
  const navigate = useNavigate();

  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editReview, setEditReview] = useState(null);

  const [alert, setAlert] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const token = localStorage.getItem("token");
  const isLoggedIn = !!token;

  const loadReviews = async () => {
    try {
      setLoading(true);
      const [reviewsRes, summaryRes] = await Promise.all([
        fetchAllReviews(),
        fetchReviewSummary(),
      ]);
      setReviews(reviewsRes.data || []);
      setAverageRating(summaryRes.data?.averageRating || 0);
      setTotalReviews(summaryRes.data?.totalReviews || 0);
    } catch (err) {
      setAlert({ message: "Failed to load reviews" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const handleAddReview = () => {
    if (!isLoggedIn) { navigate("/login"); return; }
    setEditReview(null);
    setShowModal(true);
  };

  const handleSubmitReview = async ({ rating, review }) => {
    try {
      await addOrUpdateReview({ rating, review });
      setShowModal(false);
      setEditReview(null);
      loadReviews();
    } catch (err) {
      setAlert({ message: "Failed to submit review" });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMyReview();
      setConfirmDelete(false);
      loadReviews();
    } catch (err) {
      setAlert({ message: "Failed to delete review" });
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <p className="reviews-loading">Loading reviews...</p>
      </>
    );
  }

  return (
    <>
      <Navbar />

      <div className="reviews-page">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="reviews-header"
        >
          <h2>⭐ User Reviews</h2>

          <div className="rating-summary">
            {[1, 2, 3, 4, 5].map((i) =>
              i <= Math.round(averageRating) ? (
                <FaStar key={i} />
              ) : (
                <FaRegStar key={i} />
              )
            )}
            <strong>{averageRating.toFixed(1)} / 5</strong>
            <span>({totalReviews} reviews)</span>
          </div>

          <button className="add-review-btn" onClick={handleAddReview}>
            ✍️ Add your review
          </button>
        </motion.div>

        <div className="reviews-list">
          {reviews.map((review) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              className="review-card"
            >
              <div className="review-header">
                <div className="review-user">
                  <strong>{review.userName || "User"}</strong>
                  {review.verified && (
                    <span className="verified-badge">
                      <FaCheckCircle size={12} /> Verified
                    </span>
                  )}
                </div>
                <span className="review-date">
                  {new Date(review.createdAt).toDateString()}
                </span>
              </div>

              <div className="review-stars">
                {[1, 2, 3, 4, 5].map((i) =>
                  i <= review.rating ? (
                    <FaStar key={i} />
                  ) : (
                    <FaRegStar key={i} />
                  )
                )}
              </div>

              {review.review && (
                <p className="review-text">{review.review}</p>
              )}

              {review.mine && (
                <div className="review-actions">
                  <button onClick={() => { setEditReview(review); setShowModal(true); }}>
                    Edit
                  </button>
                  <button className="danger" onClick={() => setConfirmDelete(true)}>
                    Delete
                  </button>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {confirmDelete && (
          <div className="modal-overlay">
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="modal-box"
            >
              <h3>Delete review?</h3>
              <p>This action cannot be undone.</p>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setConfirmDelete(false)}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={handleDelete}>
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {alert && (
        <div className="toast">
          <p>{alert.message}</p>
        </div>
      )}

      {showModal && (
        <AddReviewModal
          open={showModal}
          onClose={() => { setShowModal(false); setEditReview(null); }}
          onSubmit={handleSubmitReview}
          initialRating={editReview?.rating || 0}
          initialComment={editReview?.review || ""}
          isEdit={!!editReview}
        />
      )}
    </>
  );
};

export default ReviewsPage;
